import {type Dispatch, useState} from 'react';

import {useSlackShoutouts} from '../../../shared/api/useSlackShoutouts';
import type {EditorAction} from '../editorState';
import {defaultSince, overLimit, toImportedOption} from '../slackImport';
import {SlackImportDialog} from './slackImportDialog';

// Every loaded message starts picked; only the unticked ones are state.
export const SlackImportContainer = ({
    existing,
    dispatch,
    onClose,
}: {
    /** Options already on the vote, which count towards its limit. */
    existing: number;
    dispatch: Dispatch<EditorAction>;
    onClose: () => void;
}) => {
    const [since, setSince] = useState(() => defaultSince(new Date()));
    const [loadedSince, setLoadedSince] = useState<string | null>(null);
    const [unticked, setUnticked] = useState<ReadonlySet<string>>(new Set());
    const query = useSlackShoutouts(loadedSince);
    const shoutouts = query.data?.shoutouts ?? null;
    const picked = new Set((shoutouts ?? []).map((s) => s.ts).filter((ts) => !unticked.has(ts)));

    return (
        <SlackImportDialog
            since={since}
            onSince={setSince}
            onLoad={() => {
                setUnticked(new Set());
                // Same date again is a retry, say after inviting the bot: the key alone would not refetch.
                if (since === loadedSince) void query.refetch();
                else setLoadedSince(since);
            }}
            loading={query.isFetching}
            error={query.error}
            shoutouts={shoutouts}
            loadedSince={loadedSince}
            picked={picked}
            tooMany={overLimit(existing, picked.size)}
            onToggle={(ts) =>
                setUnticked((prev) => {
                    const next = new Set(prev);
                    if (next.has(ts)) next.delete(ts);
                    else next.add(ts);
                    return next;
                })
            }
            onAdd={() => {
                dispatch({
                    type: 'importOptions',
                    options: (shoutouts ?? [])
                        .filter((s) => picked.has(s.ts))
                        .map((s) => toImportedOption(s, crypto.randomUUID())),
                });
                onClose();
            }}
            onClose={onClose}
        />
    );
};
